import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useAuth } from '../../hooks/useAuth';
import { usePublisher } from '../../hooks/usePublisher';
import { useTRPC } from '../../hooks/useTRPC';
import { captureAndReportError } from '../../utils/errorReporting';
import { getSafeFromLocation } from '../../utils/navigation';
import { ConfirmIndividualDetails } from './individual/ConfirmIndividualDetails';
import { FormData as IndividualFormData } from './individual/FormData';
import { ConfirmOrganizationDetails } from './organization/ConfirmOrganizationDetails';
import { FormData as OrgFormData } from './organization/FormData';

function isOrgData(
  formData: IndividualFormData | OrgFormData,
): formData is OrgFormData {
  return 'publisherName' in formData;
}

interface Props {
  className?: string;
  formData: OrgFormData | IndividualFormData;
  onBack?(): void;
}

export function Confirm(props: Props) {
  const [, { refreshUser }] = useAuth();
  const { refresh: refreshPublisher } = usePublisher();
  const navigate = useNavigate();
  const location = useLocation();
  const trpc = useTRPC();
  const [completeVerificationNow, setCompleteVerificationNow] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContinue = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      await refreshUser();
      await refreshPublisher();

      const publisherResult = await trpc.getActivePublisher.query(undefined);
      if (publisherResult._tag === 'Left') {
        toast.error(
          publisherResult.left.message ||
            'Failed to load your active publisher',
        );
        return;
      }
      const publisher = publisherResult.right;

      if (completeVerificationNow) {
        navigate(
          publisher.publisherType === 'Individual'
            ? '/dashboard/submit-kyc'
            : '/dashboard/submit-kyb',
          { replace: true },
        );
        return;
      }

      navigate(getSafeFromLocation(location.state?.from) ?? '/dashboard', {
        replace: true,
      });
    } catch (error) {
      captureAndReportError('Error finishing publisher creation:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isOrgData(props.formData)) {
    return (
      <ConfirmOrganizationDetails
        className={props.className}
        formData={props.formData}
        completeVerificationNow={completeVerificationNow}
        onCompleteVerificationChange={(value) =>
          setCompleteVerificationNow(value)
        }
        submitting={isSubmitting}
        onContinue={handleContinue}
        onBack={props.onBack}
      />
    );
  } else {
    return (
      <ConfirmIndividualDetails
        className={props.className}
        formData={props.formData}
        completeVerificationNow={completeVerificationNow}
        onCompleteVerificationChange={(value) =>
          setCompleteVerificationNow(value)
        }
        submitting={isSubmitting}
        onContinue={handleContinue}
        onBack={props.onBack}
      />
    );
  }
}
